import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box, CircularProgress, Typography, styled } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddIcon from "@mui/icons-material/Add";
import { Suggestion } from "@interfaces";
import { useNavigate } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";

const SuggestionsContainer = styled(Box, {
  shouldForwardProp: (prop: string) => prop !== "focused",
})<{ focused: boolean }>(({ focused }) => ({
  display: focused ? "flex" : "none",
  height: "100%",
  flexDirection: "column",
}));

const SuggestionRow = styled("button")(({ theme }) => ({
  border: "none",
  background: theme.palette.background.paper,
  padding: "12px 16px",
  width: "100%",
  cursor: "pointer",
  display: "flex",
  justifyContent: "flex-start",
  gap: "16px",
  alignItems: "center",
  borderBottom: "1px solid #eaeaea",
  "&:hover": {
    background: theme.palette.background.disabled,
  },
}));

const CenteredSuggestionRow = styled(SuggestionRow)({
  justifyContent: "center",
  gap: "8px",
});

const SuggestionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  textAlign: "left",
  color: theme.palette.text.secondary,
}));

const SecondarySuggestionTitle = styled(SuggestionTitle)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 400,
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  fontSize: "24px",
  width: "24px",
  height: "24px",
  color: theme.palette.icon.secondary,
}));

const EmptyRequest = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
  gap: "16px",
  color: theme.palette.text.secondary,
  borderBottom: "1px solid #eaeaea",
  backgroundColor: theme.palette.background.paper,
}));

const StyledInfoIconEmpty = styled(InfoOutlinedIcon)(({ theme }) => ({
  fontSize: "24px",
  color: theme.palette.icon.disabled,
}));

const StyledErrorIcon = styled(ErrorOutlinedIcon)(({ theme }) => ({
  fontSize: "24px",
  color: theme.palette.error.main,
}));

const StyledAddIcon = styled(AddIcon)(({ theme }) => ({
  color: theme.palette.icon.primary,
}));

const CenteredTypography = styled(Typography)({
  textAlign: "center",
});

interface Props {
  className?: string;
  focused?: boolean;
  setFocused?: Dispatch<SetStateAction<boolean>>;
  searchText: string;
  preliminarySearchResults: Suggestion[];
  addManuallyItemText?: string;
  onItemClick: (item: Suggestion) => void;
  locationState?: any;
  showAddManuallyItemButton?: boolean;
  showEmptyResultsMessage?: boolean;
  showErrorResultsMessage?: boolean;
  showLoader?: boolean;
}

export const SearchSuggestions = ({
  className,
  showAddManuallyItemButton,
  showEmptyResultsMessage,
  showErrorResultsMessage,
  showLoader,
  focused = false,
  setFocused = () => {},
  searchText,
  preliminarySearchResults,
  onItemClick,
  addManuallyItemText,
  locationState,
}: Props) => {
  const navigate = useNavigate();

  const suggestionsInitialState = useMemo(() => {
    return {
      preliminarySearchResults: preliminarySearchResults.slice(0, 8),
      showMoreResultsButton: preliminarySearchResults.length > 8,
    };
  }, [preliminarySearchResults]);

  const [preliminarySearchResultsToShow, setPreliminarySearchResultsToShow] =
    useState<Suggestion[]>(suggestionsInitialState.preliminarySearchResults);
  const [showMoreResultsButton, setShowMoreResultsButton] = useState(
    suggestionsInitialState.showMoreResultsButton,
  );

  const handleAddManually = useCallback(() => {
    navigate("../añadir-manual", {
      state: {
        searchedQuery: searchText,
        ...locationState,
      },
      relative: "path",
    });
  }, [locationState, navigate, searchText]);

  const handleShowMoreResults = useCallback(() => {
    setPreliminarySearchResultsToShow(preliminarySearchResults);
    setShowMoreResultsButton(false);
  }, [preliminarySearchResults]);

  useEffect(() => {
    function limitSuggestionsOnSearchTextChange() {
      setPreliminarySearchResultsToShow(
        suggestionsInitialState.preliminarySearchResults,
      );
      setShowMoreResultsButton(suggestionsInitialState.showMoreResultsButton);
    }
    limitSuggestionsOnSearchTextChange();
  }, [suggestionsInitialState]);

  return (
    <SuggestionsContainer focused={focused} className={className}>
      <div
        id="preliminary-search-results"
        style={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}
      >
        {preliminarySearchResultsToShow.map((item, i) => (
          <SuggestionRow
            key={`preliminary-${i}`}
            onClick={() => {
              setFocused(false);
              onItemClick(item);
            }}
          >
            <IconContainer>
              <SearchIcon />
            </IconContainer>
            <SuggestionTitle>{item.text}</SuggestionTitle>
          </SuggestionRow>
        ))}
        {showEmptyResultsMessage && (
          <EmptyRequest>
            <StyledInfoIconEmpty />
            <CenteredTypography variant="body1">
              No hay sugerencias para tu búsqueda
            </CenteredTypography>
          </EmptyRequest>
        )}
        {showErrorResultsMessage && (
          <EmptyRequest>
            <StyledErrorIcon color="error" />
            <CenteredTypography variant="body1">
              Error al obtener las sugerencias de tu búsqueda
            </CenteredTypography>
          </EmptyRequest>
        )}
        {showLoader && (
          <EmptyRequest>
            <CircularProgress size={24} />
          </EmptyRequest>
        )}
      </div>
      {showMoreResultsButton && (
        <CenteredSuggestionRow onClick={handleShowMoreResults}>
          <SecondarySuggestionTitle>
            Mostrar más resultados
          </SecondarySuggestionTitle>
          <IconContainer>
            <KeyboardArrowDownIcon />
          </IconContainer>
        </CenteredSuggestionRow>
      )}
      {showAddManuallyItemButton && (
        <CenteredSuggestionRow onClick={handleAddManually}>
          <Typography color="primary" fontWeight={500}>
            {addManuallyItemText}
          </Typography>
          <StyledAddIcon />
        </CenteredSuggestionRow>
      )}
    </SuggestionsContainer>
  );
};
